
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Mail,
  ShieldCheck,
} from "lucide-react";

import logo from "../assets/logo.png";

const exploreLinks = [
  { label: "Home", to: "/" },
  { label: "Saree Collection", to: "/#collection" },
  { label: "How It Works", to: "/#how-it-works" },
  { label: "Earnings", to: "/#earnings" },
  { label: "FAQs", to: "/#faqs" },
];

const businessLinks = [
  { label: "For Shop Owners", to: "/#shop-owners" },
  { label: "Become a Reseller", to: "/signup" },
  { label: "Sign In", to: "/signin" },
  { label: "Sign Up", to: "/signup" },
];

function FooterLink({ to, children }) {
  return (
    <Link
      to={to}
      className="group inline-flex w-fit items-center gap-2 text-sm text-white/60 transition-colors duration-300 hover:text-[#e5c28a]"
    >
      <span>{children}</span>
      <ArrowUpRight
        size={13}
        className="opacity-0 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:opacity-100"
      />
    </Link>
  );
}

function FooterColumn({ title, children }) {
  return (
    <div>
      <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-[#e5c28a]">
        {title}
      </h3>

      <div className="mt-5 flex flex-col items-start gap-3.5">
        {children}
      </div>
    </div>
  );
}

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative isolate overflow-hidden bg-[#30040f] text-white">
      {/* Decorative background */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-32 -top-32 -z-10 h-80 w-80 rounded-full border border-[#e5c28a]/10"
      />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-20 -top-20 -z-10 h-56 w-56 rounded-full border border-[#e5c28a]/10"
      />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-28 -left-24 -z-10 h-64 w-64 rounded-full bg-[#a51d47]/10 blur-3xl"
      />

      <div className="mx-auto max-w-[1240px] px-5 pb-7 pt-12 sm:px-8 sm:pb-8 sm:pt-16 lg:px-10">
        {/* Main footer */}
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-[1.35fr_0.8fr_1fr_1.1fr] lg:gap-10">
          {/* Brand */}
          <div>
            <Link
              to="/"
              aria-label="Vasthra home"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="group inline-flex items-center gap-3"
            >
              <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-[#e5c28a]/30 bg-white p-1.5 shadow-sm transition duration-300 group-hover:-translate-y-0.5 group-hover:shadow-lg group-hover:shadow-black/20 sm:h-16 sm:w-16">
                <img
                  src={logo}
                  alt="Vasthra logo"
                  className="h-full w-full object-contain"
                />
              </span>

              <span>
                <span className="block font-serif text-2xl font-bold tracking-wide text-white">
                  Vasthra
                </span>

                <span className="mt-1 block text-[9px] uppercase tracking-[0.2em] text-[#e5c28a]/80">
                  Tradition meets opportunity
                </span>
              </span>
            </Link>

            <p className="mt-5 max-w-xs text-sm leading-7 text-white/60">
              Discover beautiful sarees, connect with shop owners, and
              take the first step towards your own saree reselling journey.
            </p>

            <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-2 text-xs text-white/65">
              <ShieldCheck
                size={15}
                className="shrink-0 text-[#e5c28a]"
              />
              <span>Built around trust and transparency</span>
            </div>
          </div>

          {/* Explore */}
          <FooterColumn title="Explore">
            {exploreLinks.map((item) => (
              <FooterLink
                key={item.to + item.label}
                to={item.to}
              >
                {item.label}
              </FooterLink>
            ))}
          </FooterColumn>

          {/* Business */}
          <FooterColumn title="For Businesses">
            {businessLinks.map((item) => (
              <FooterLink
                key={item.to + item.label}
                to={item.to}
              >
                {item.label}
              </FooterLink>
            ))}
          </FooterColumn>

          {/* Contact */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-[#e5c28a]">
              Let&apos;s Connect
            </h3>

            <p className="mt-5 text-sm leading-7 text-white/60">
              Have questions about registration, saree collections, or
              how the platform works? We&apos;re here to help.
            </p>

            <Link
              to="/#contact"
              className="group mt-5 inline-flex items-center gap-3 rounded-full bg-[#e5c28a] px-5 py-3 text-sm font-semibold text-[#30040f] transition duration-300 hover:-translate-y-0.5 hover:bg-[#f0d7a9] hover:shadow-lg hover:shadow-black/20"
            >
              Get in touch

              <ArrowRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </Link>

            <div className="mt-6 flex flex-col gap-3 text-sm text-white/60">
              <Link
                to="/#contact"
                className="inline-flex w-fit items-center gap-2 transition hover:text-[#e5c28a]"
              >
                <Mail size={15} className="shrink-0 text-[#e5c28a]" />
                vasthraonline98@gmail.com
              </Link>

            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="my-9 h-px bg-gradient-to-r from-transparent via-[#e5c28a]/25 to-transparent sm:my-12" />

        {/* Bottom bar — no Back to Top here */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs leading-6 text-white/45">
            <p>
              © {currentYear} Vasthra. All rights reserved.
            </p>

            <p className="mt-1">
              Made for those who turn tradition into opportunity.
            </p>
          </div>

          <Link
            to="/#contact"
            className="inline-flex w-fit items-center gap-2 text-xs text-white/50 transition hover:text-[#e5c28a]"
          >
            Need help? Contact us
            <ArrowUpRight size={13} />
          </Link>
        </div>
      </div>
    </footer>
  );
}
